import React, { useRef } from 'react';
import { connect } from 'react-redux';
import {
    setHoveringGroupElementID as setHoveringGroupElementIDAction,
    setSelectedMapElementID as setSelectedMapElementIDAction,
    setMultiSelectedMapElementID as setMultiSelectedMapElementIDAction,
    setDraggingData as setDraggingDataAction,
} from '../../../../actions/reduxActions/graphEditor';
import { pickByPrefix, getParentOffset, getParents, getSystemFlowName } from '../../utils';

import '../../../../../css/graph/group-element.less';
import GroupElementBase from './GroupElementBase';

const resizeCornerSize = '20';
const resizeThickness = '15';
const halfThickness = resizeThickness / 2;

export const GroupElement = (props) => {

    const {
        groupElement,
        hoveringGroupElementId,
        setHoveringGroupElementID,
        draggingOverGroupElementId,
        selectedElementIds,
        setSelectedMapElementID,
        setMultiSelectedMapElementID,
        dragging,
        setDraggingData,
        openConfig,
        flowId,
        editingToken,
        allParentsOpen,
    } = props;

    // if this element doesn't have a group element, then don't render
    // this can happen when we delete a group, and we delete the groupElement in redux state before the groupElementIdFlowId reference,
    // or when we add a groupElementIdFlowId reference, before adding the groupElement in redux state
    if (!groupElement) return (null);

    // if one of this element's parents is closed then don't render this
    if (allParentsOpen === false) return (null);

    const { id, x, y, height, width, developerName, elementType, isOpen } = groupElement;

    const groupEl = useRef(null);
    /*
         XL     XR
        ┏━┵─────┶━┓
    YT  ┦         ┦
        │         │
    YB  ┧         ┧
        ┗━┵─────┶━┛
    */
    const xLeft = Math.min(resizeCornerSize, width / 2);
    const xRight = Math.max(width - resizeCornerSize, width / 2);
    const yTop = Math.min(resizeCornerSize, height / 2);
    const yBottom = Math.max(height - resizeCornerSize, height / 2);

    const resizeHandles = [
        /* ━━ Top */
        { side: 'top', cursor: 'n-resize', edges: [{ x: xLeft, y: -halfThickness, width: xRight - xLeft, height: resizeThickness }] },
        /* ━┓ Top Right */
        { 
            side: 'top,right',
            cursor: 'ne-resize',
            edges: [
                { x: xRight, y: -halfThickness, width: xLeft + halfThickness, height: resizeThickness },
                { x: width - halfThickness, y: halfThickness, width: resizeThickness, height: yTop - halfThickness },
            ],
        },
        /* ┃  Right */
        { side: 'right', cursor: 'e-resize', edges: [{ x: width - halfThickness, y: yTop, width: resizeThickness, height: yBottom - yTop }] },
        /* ━┛ Bottom Right */
        { 
            side: 'bottom,right',
            cursor: 'se-resize',
            edges: [
                { x: xRight, y: height - halfThickness, width: xLeft + halfThickness, height: resizeThickness },
                { x: width - halfThickness, y: yBottom, width: resizeThickness, height: yTop - halfThickness },
            ],
        },
        /* ━━ Bottom */
        { side: 'bottom', cursor: 's-resize', edges: [{ x: xLeft, y: height - halfThickness, width: xRight - xLeft, height: resizeThickness }] },
        /* ┗━ Bottom Left */
        { 
            side: 'bottom,left',
            cursor: 'sw-resize',
            edges: [
                { x: -halfThickness, y: height - halfThickness, width: xLeft + halfThickness, height: resizeThickness },
                { x: -halfThickness, y: yBottom, width: resizeThickness, height: yTop - halfThickness },
            ],
        },
        /* ┃  Left */
        { side: 'left', cursor: 'w-resize', edges: [{ x: -halfThickness, y: yTop, width: resizeThickness, height: yBottom - yTop }] },
        /* ┏━ Top Left */
        { 
            side: 'top,left',
            cursor: 'nw-resize',
            edges: [
                { x: -halfThickness, y: -halfThickness, width: xLeft + halfThickness, height: resizeThickness },
                { x: -halfThickness, y: halfThickness, width: resizeThickness, height: yTop - halfThickness },
            ],
        },
    ];

    const parents = pickByPrefix('parent_', props);

    const parentOffset = getParentOffset(parents);

    const setSelected = newID => setSelectedMapElementID(newID);
    const setMultiSelected = newID => setMultiSelectedMapElementID(newID);
    const setHover = newID => setHoveringGroupElementID(newID);

    const openDeletionSystemFlow = () => openConfig(
        'Delete Group Element',
        {
            editingToken,
            flowId,
            id,
        },
        null,
        null,
        null,
        null,
    );

    return (
        <GroupElementBase
            groupRef={groupEl}
            groupElement={groupElement}
            startDrag={e => setDraggingData({ dragType: 'group', elementId: id, previousElementPosition: { x, y }, previousMousePosition: { x: e.clientX, y: e.clientY } })}
            onSelect={e => e.ctrlKey ? setMultiSelected(id) : setSelected(id)}
            onOver={() => setHover(id)}
            onOut={() => setHover(null)}
            onDoubleClick={
                () => openConfig(
                    getSystemFlowName(elementType),
                    {
                        editingToken,
                        flowId,
                        id,
                        elementType,
                        x,
                        y,
                    },
                    null,
                    null,
                    null,
                    { id: elementType },
                )
            }
            onDelete={openDeletionSystemFlow}
            isSelected={selectedElementIds.includes(id)}
            isHovered={hoveringGroupElementId === id}
            resizeHandles={
                resizeHandles.map(({ edges, side, cursor }) => (
                    <g
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            setDraggingData({ dragType: 'resize', elementId: id, handleSide: side, previousElementPosition: { x, y, width, height }, previousMousePosition: { x: e.clientX, y: e.clientY } });
                        }}
                        style={{ cursor }}
                        className="resize-handle"
                    > 
                        {edges.map(edge => (
                            <rect fill="transparent" x={edge.x} y={edge.y} width={edge.width} height={edge.height} />
                        ))}
                    </g>
                ))
            }
            isDragging={dragging && dragging.elementId === id}
            draggingOver={draggingOverGroupElementId === id}
            height={height}
            width={width}
            developerName={developerName}
            x={x + parentOffset.x}
            y={y + parentOffset.y}
            elementType={elementType}
            id={id}
            flowId={flowId}
            editingToken={editingToken}
            isOpen={isOpen}
        />
    );
};

export default connect(
    ({ groupElements, graphEditor }, ownProps) => {

        const groupElement = groupElements.find(ge => ge.id === ownProps.groupElementId);

        const props = {
            groupElement,
            ...graphEditor,
        };

        let allParentsOpen = true;
        getParents(groupElement, groupElements).forEach((parent) => {
            props[`parent_${parent.id}`] = parent;
            if (parent.isOpen === false) {
                allParentsOpen = false;
            }
        });

        props.allParentsOpen = allParentsOpen;

        return props;
    },
    {
        setSelectedMapElementID: setSelectedMapElementIDAction,
        setMultiSelectedMapElementID: setMultiSelectedMapElementIDAction,
        setHoveringGroupElementID: setHoveringGroupElementIDAction,
        setDraggingData: setDraggingDataAction,
    },
)(GroupElement);
